import apiAgent from "@/app/api/apiAgent";
import { useQuery } from "@tanstack/react-query";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@/app/common/common-components/ui/DropdownMenu";
import { ChevronDown } from "lucide-react";
import { ReactNode } from "react";

type PipelineStageDropdownMenuProps = {
  type: "deal" | "contact";
  officeId: string;
  disabled?: boolean;
  placeholder?: string;
  onStageClick?: (pipelineId: string, stageId: string) => void;
  children?: ReactNode;
  title?: string;
};

const PipelineStageDropdownMenu = ({
  type,
  officeId,
  disabled,
  placeholder,
  onStageClick,
  children,
  title,
}: PipelineStageDropdownMenuProps) => {
  const { data: pipelines, isLoading } = useQuery({
    queryKey: ["pipelines-dropdown", officeId],
    queryFn: () => apiAgent.Pipeline.getPipelines(type, officeId),
  });

  const isDisabled =
    !pipelines || pipelines.length === 0 || isLoading || disabled;

  const handleStageClick = (pipelineId: string, stageId: string) => {
    onStageClick?.(pipelineId, stageId);
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild disabled={isDisabled}>
        {children || (
          <button className="tw-inline-flex tw-h-10 tw-items-center tw-justify-center tw-rounded-md tw-border tw-border-input tw-bg-background tw-px-3 tw-py-2 tw-text-sm tw-font-medium tw-ring-offset-background tw-transition-colors focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-ring focus-visible:tw-ring-offset-2 disabled:tw-pointer-events-none disabled:tw-opacity-50 [&>span]:tw-line-clamp-1">
            {placeholder || "Select pipeline & stage"}
            <ChevronDown className="tw-ml-2 tw-h-4 tw-w-4" />
          </button>
        )}
      </DropdownMenuTrigger>
      <DropdownMenuContent className="tw-w-56">
        {title && <DropdownMenuLabel>{title}</DropdownMenuLabel>}
        {pipelines?.map((pipeline) => (
          <DropdownMenuSub key={pipeline.pipeline_id}>
            <DropdownMenuSubTrigger>
              {pipeline.pipeline_name}
            </DropdownMenuSubTrigger>
            <DropdownMenuSubContent>
              {pipeline.milestone?.map((stage) => (
                <DropdownMenuItem
                  key={stage.milestone_id}
                  onClick={() =>
                    handleStageClick(pipeline.pipeline_id, stage.milestone_id)
                  }
                >
                  {stage.milestone_name}
                </DropdownMenuItem>
              ))}
            </DropdownMenuSubContent>
          </DropdownMenuSub>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default PipelineStageDropdownMenu;
